import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Select, Image, Spin, Popconfirm, Tooltip, message } from "antd";
import { Editor } from "@tinymce/tinymce-react";
import { Search, X, Eye, Pencil, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import CommentItem from "./CommentItem";
import useAuthStore from "../../../store/authStore";
import {
    getCustomerWorkLogs,
    createCustomerWorkLog,
    updateCustomerWorkLog,
    deleteCustomerWorkLog,
    WorkLogItem,
    uploadCustomerNotePhotos,
    deleteCustomerNotePhoto,
} from "../../../api/CustomerWorkLog";

const PAGE_SIZE = 20;
const MAX_PHOTO_SIZE = 10 * 1024 * 1024; // 10MB/ảnh theo API photos-note

interface NotePhoto {
    uid: string;
    id?: number;
    url: string;
    uploading?: boolean;
}

/** ID nhân viên đang đăng nhập — dùng để chỉ cho sửa/xóa bản ghi của chính mình. */
const currentUserId = (() => {
    try {
        const u = JSON.parse(localStorage.getItem("user") || "null");
        const id = Number(u?.user_id);
        return Number.isFinite(id) ? id : null;
    } catch {
        return null;
    }
})();

/** hideFilters: ẩn ô tìm bình luận + lọc nhân viên (hồ sơ khách tiềm năng Telesales) */
export default function CommentSection({ customerId, hideFilters = false }: { customerId?: number; hideFilters?: boolean }) {
    const { isSuperAdmin } = useAuthStore();
    const [content, setContent] = useState("");
    const [sending, setSending] = useState(false);
    const [search, setSearch] = useState("");
    const [employee, setEmployee] = useState<number>();

    // Sửa tại chỗ: mỗi lúc chỉ một bản ghi được mở khung soạn thảo
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingContent, setEditingContent] = useState("");
    const [savingEdit, setSavingEdit] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    // Lịch sử công việc (API /customer-work-logs)
    const [logs, setLogs] = useState<WorkLogItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    // Ảnh dán từ clipboard — upload ngay qua API photos-note, hiển thị dưới ô nhập
    const [notePhotos, setNotePhotos] = useState<NotePhoto[]>([]);
    const customerIdRef = useRef<number | undefined>(customerId);
    customerIdRef.current = customerId;
    const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const fetchLogs = (p = 1, append = false) => {
        if (!customerId) return;
        setLoading(true);
        getCustomerWorkLogs(customerId, p, PAGE_SIZE).then((res) => {
            if (res.status && res.data) {
                setLogs((prev) => (append ? [...prev, ...(res.data.items || [])] : res.data.items || []));
                setTotal(res.data.total || 0);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        setLogs([]);
        setTotal(0);
        setPage(1);
        fetchLogs(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    // Đổi khách -> dọn khu ảnh đang dán dở (ảnh dán chỉ thuộc bản ghi sắp gửi)
    useEffect(() => {
        setNotePhotos((prev) => {
            prev.forEach((p) => { if (!p.id) URL.revokeObjectURL(p.url); });
            return [];
        });
    }, [customerId]);

    // Dán ảnh từ clipboard trong editor -> đưa vào danh sách bên dưới (không chèn vào nội dung)
    const handlePastedFiles = (files: File[]) => {
        const cid = customerIdRef.current;
        if (!cid) return;
        files.forEach(async (file) => {
            const uid = newUid();
            const url = URL.createObjectURL(file);
            setNotePhotos((prev) => [...prev, { uid, url, uploading: true }]);
            const res = await uploadCustomerNotePhotos(cid, [file]);
            const uploaded = res.status && res.data && res.data[0];
            URL.revokeObjectURL(url);
            if (uploaded) {
                setNotePhotos((prev) =>
                    prev.map((p) => (p.uid === uid ? { uid, id: uploaded.id, url: uploaded.image } : p))
                );
            } else {
                setNotePhotos((prev) => prev.filter((p) => p.uid !== uid));
                message.error(res.msg || "Tải ảnh lên thất bại");
            }
        });
    };

    const removeNotePhoto = async (uid: string) => {
        const p = notePhotos.find((x) => x.uid === uid);
        if (!p || p.uploading) return;
        if (p.id) {
            const res = await deleteCustomerNotePhoto(p.id);
            if (!res.status) { message.error(res.msg || "Xóa ảnh thất bại"); return; }
        }
        setNotePhotos((prev) => prev.filter((x) => x.uid !== uid));
    };

    const handleSend = async () => {
        const cid = customerId;
        if (!cid) return;
        const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        if (!plainText && notePhotos.length === 0) {
            message.warning("Vui lòng nhập nội dung");
            return;
        }
        if (notePhotos.some((p) => p.uploading)) {
            message.warning("Vui lòng chờ ảnh tải lên xong");
            return;
        }
        // Nhúng ảnh đã dán vào nội dung bản ghi (content là HTML) để danh sách hiển thị kèm ảnh
        const imagesHtml = notePhotos.length
            ? `<p>${notePhotos
                .map((p) => `<img src="${p.url}" style="max-width:140px;border-radius:8px;margin:4px 4px 0 0" />`)
                .join("")}</p>`
            : "";
        setSending(true);
        const res = await createCustomerWorkLog(cid, `${content}${imagesHtml}`);
        setSending(false);
        if (res.status) {
            message.success(res.msg || "Thêm lịch sử công việc thành công");
            setContent("");
            setNotePhotos([]); // ảnh đã thuộc về bản ghi -> clear khu chờ
            setPage(1);
            fetchLogs(1);
        } else {
            message.error(res.msg || "Gửi thất bại");
        }
    };

    /**
     * Chỉ người tạo mới được sửa/xóa ghi chú của mình; Super Admin thì toàn quyền.
     * Lịch sử công việc là dấu vết trao đổi với khách, để ai cũng sửa được của nhau
     * thì mất giá trị đối chiếu.
     */
    const canManage = (item: WorkLogItem) =>
        isSuperAdmin || (currentUserId != null && item.created_by_id === currentUserId);

    const startEdit = (item: WorkLogItem) => {
        setEditingId(item.id);
        setEditingContent(item.content || "");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingContent("");
    };

    const saveEdit = async () => {
        if (editingId == null) return;
        const plain = editingContent.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        const hasImage = /<img\b/i.test(editingContent);
        if (!plain && !hasImage) {
            message.warning("Nội dung không được để trống");
            return;
        }
        setSavingEdit(true);
        const res = await updateCustomerWorkLog(editingId, editingContent);
        setSavingEdit(false);
        if (!res.status) {
            message.error(res.msg || "Cập nhật thất bại");
            return;
        }
        // Cập nhật ngay trong danh sách, khỏi tải lại cả trang -> không mất vị trí cuộn
        setLogs((prev) =>
            prev.map((l) =>
                l.id === editingId
                    ? { ...l, content: editingContent, updated_at: res.data?.updated_at || new Date().toISOString() }
                    : l
            )
        );
        cancelEdit();
        message.success(res.msg || "Cập nhật lịch sử công việc thành công");
    };

    const handleDelete = async (item: WorkLogItem) => {
        setDeletingId(item.id);
        const res = await deleteCustomerWorkLog(item.id);
        setDeletingId(null);
        if (!res.status) {
            message.error(res.msg || "Xóa thất bại");
            return;
        }
        if (editingId === item.id) cancelEdit();
        setLogs((prev) => prev.filter((l) => l.id !== item.id));
        setTotal((prev) => Math.max(0, prev - 1));
        message.success(res.msg || "Xóa lịch sử công việc thành công");
    };

    // Bộ lọc nhân viên từ danh sách người tạo đã tải
    const employeeOptions = useMemo(() => {
        const map = new Map<number, string>();
        logs.forEach((l) => {
            const name = (l.creator as any)?.name || (l.creator as any)?.fullname;
            if (l.created_by_id && name && !map.has(l.created_by_id)) map.set(l.created_by_id, name);
        });
        return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    }, [logs]);

    const comments = useMemo(() => {
        const kw = search.toLowerCase();
        return logs.filter((x) => {
            const name = ((x.creator as any)?.name || (x.creator as any)?.fullname || "").toLowerCase();
            const text = (x.content || "").replace(/<[^>]*>/g, " ").toLowerCase();
            const matchSearch = !kw || text.includes(kw) || name.includes(kw);
            const matchEmployee = employee == null || x.created_by_id === employee;
            return matchSearch && matchEmployee;
        });
    }, [logs, search, employee]);

    return (
        <div
            className="
                overflow-hidden
            "
        >
            {/* ================= Editor ================= */}

            <Editor
                // Bản self-host (GPL) trong public/tinymce — không cần API key cloud
                tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
                licenseKey="gpl"
                value={content}
                onEditorChange={setContent}
                init={{
                    height: 180,
                    menubar: false,

                    branding: false,

                    resize: false,

                    skin: document.documentElement.classList.contains("dark")
                        ? "oxide-dark"
                        : "oxide",

                    content_css:
                        document.documentElement.classList.contains("dark")
                            ? "dark"
                            : "default",

                    plugins: [
                        "link",
                        "lists",
                        "image",
                        "table",
                        "code",
                        "fullscreen",
                        "preview",
                    ],

                    toolbar:
                        "undo redo | bold italic underline strikethrough | fontfamily fontsize | forecolor backcolor | alignleft aligncenter alignright | bullist numlist outdent indent | table image link | preview fullscreen code",

                    placeholder:
                        "Nhập nội dung hoặc gõ # để thêm hashtag",

                    // Không chèn ảnh dán vào nội dung — đưa xuống danh sách ảnh bên dưới
                    paste_data_images: false,
                    setup: (editor: any) => {
                        editor.on("paste", (e: ClipboardEvent) => {
                            const items = e.clipboardData?.items;
                            if (!items) return;
                            const files: File[] = [];
                            let tooLarge = false;
                            Array.from(items).forEach((item) => {
                                if (item.type.startsWith("image/")) {
                                    const file = item.getAsFile();
                                    if (!file) return;
                                    if (file.size > MAX_PHOTO_SIZE) { tooLarge = true; return; }
                                    files.push(file);
                                }
                            });
                            if (tooLarge) message.error("Ảnh vượt quá 10MB");
                            if (!files.length) return;
                            e.preventDefault();
                            handlePastedFiles(files);
                        });
                    },
                }}
            />

            {notePhotos.length > 0 && (

                <div className="flex flex-wrap gap-2 py-3 text-000000e0">

                    {notePhotos.map((photo) => (

                        <div
                            key={photo.uid}
                            className="
                                relative
                                rounded-lg
                                overflow-hidden
                                border
                                dark:border-[#2a2a2a]
                                !w-[64px] !h-[64px]
                            "
                        >

                            <Image
                                src={photo.url}
                                className="!w-[64px] !h-[64px] object-contain flex items-center justify-center"
                                preview={{
                                    mask: <Eye size={18} />
                                }}
                            />

                            {photo.uploading && (
                                <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                                    <Spin size="small" />
                                </div>
                            )}

                            <button
                                className="
                                    absolute
                                    -top-1
                                    -right-1
                                    w-5
                                    h-5
                                    rounded-full
                                    bg-red-500
                                    text-white
                                "
                                onClick={() => removeNotePhoto(photo.uid)}
                            >
                                <X size={14} />
                            </button>

                        </div>

                    ))}

                </div>

            )}

            {/* ================= Toolbar ================= */}

            <div
                className="
                    flex
                    items-center
                    justify-end
                    py-3
                "
            >
                <Button
                    type="primary"
                    className="px-8"
                    loading={sending}
                    onClick={handleSend}
                >
                    Gửi
                </Button>
            </div>

            {/* ================= Filter ================= */}

            {!hideFilters && <div
                className="
                    grid
                    grid-cols-1
                    lg:grid-cols-2
                    gap-3
                    py-3
                    pt-0
                "
            >
                <Input
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tìm kiếm bình luận"
                    prefix={<Search size={16} />}
                />

                <Select
                    allowClear
                    value={employee}
                    onChange={setEmployee}
                    placeholder="Chọn nhân viên"
                    showSearch
                    optionFilterProp="label"
                    options={employeeOptions}
                />
            </div>}

            {/* ================= List ================= */}

            <Spin spinning={loading}>
                <div
                    className="
                        max-h-[420px]
                        overflow-y-auto
                    "
                >
                    {comments.map((item) => {
                        const editing = editingId === item.id;
                        return (
                            <CommentItem
                                key={item.id}
                                item={{
                                    id: item.id,
                                    avatar: "",
                                    name: (item.creator as any)?.name || (item.creator as any)?.fullname || "—",
                                    time: item.created_at ? dayjs(item.created_at).format("HH:mm - DD/MM/YYYY") : "",
                                    content: item.content,
                                    edited: !!item.updated_at && item.updated_at !== item.created_at,
                                    // Ảnh của bản ghi do API trả về (ngoài ảnh nhúng trong nội dung)
                                    images: (item.photos || []).map((p) => p.image_path).filter(Boolean),
                                }}
                                actions={
                                    canManage(item) && !editing ? (
                                        <>
                                            <Tooltip title="Sửa">
                                                <button
                                                    onClick={() => startEdit(item)}
                                                    className="p-1 rounded text-gray-500 hover:text-[#3e91ab] hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                                                >
                                                    <Pencil size={15} />
                                                </button>
                                            </Tooltip>

                                            <Popconfirm
                                                title="Xóa lịch sử công việc"
                                                description="Bản ghi này sẽ bị xóa vĩnh viễn."
                                                okText="Xóa"
                                                cancelText="Hủy"
                                                okButtonProps={{ danger: true, loading: deletingId === item.id }}
                                                onConfirm={() => handleDelete(item)}
                                            >
                                                <Tooltip title="Xóa">
                                                    <button className="p-1 rounded text-gray-500 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]">
                                                        <Trash2 size={15} />
                                                    </button>
                                                </Tooltip>
                                            </Popconfirm>
                                        </>
                                    ) : null
                                }
                            >
                                {editing ? (
                                    <>
                                        <Editor
                                            tinymceScriptSrc={`${process.env.PUBLIC_URL || ""}/tinymce/tinymce.min.js`}
                                            licenseKey="gpl"
                                            value={editingContent}
                                            onEditorChange={setEditingContent}
                                            init={{
                                                height: 160,
                                                menubar: false,
                                                branding: false,
                                                resize: false,
                                                skin: document.documentElement.classList.contains("dark") ? "oxide-dark" : "oxide",
                                                content_css: document.documentElement.classList.contains("dark") ? "dark" : "default",
                                                plugins: ["link", "lists", "image", "code"],
                                                toolbar:
                                                    "undo redo | bold italic underline | forecolor | bullist numlist | link | code",
                                                // Ảnh cũ nằm sẵn trong nội dung nên vẫn sửa/xóa được ngay trong khung này
                                                paste_data_images: false,
                                            }}
                                        />

                                        <div className="flex justify-end gap-2 mt-2">
                                            <Button size="small" onClick={cancelEdit} disabled={savingEdit}>
                                                Hủy
                                            </Button>
                                            <Button size="small" type="primary" loading={savingEdit} onClick={saveEdit}>
                                                Lưu
                                            </Button>
                                        </div>
                                    </>
                                ) : null}
                            </CommentItem>
                        );
                    })}

                    {!loading && comments.length === 0 && (
                        <div className="py-10 text-center text-slate-400 font-medium">
                            Chưa có lịch sử công việc
                        </div>
                    )}

                    {logs.length < total && (
                        <div className="py-3 text-center">
                            <Button
                                onClick={() => { const next = page + 1; setPage(next); fetchLogs(next, true); }}
                            >
                                Xem thêm ({logs.length}/{total})
                            </Button>
                        </div>
                    )}
                </div>
            </Spin>
        </div>
    );
}
